import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { Link } from '@tanstack/react-router';
import { notificationsApi, type NotifRow } from '../features/notifications/api.ts';
import { useSocketStore } from '../store/socket.ts';

export function NotificationBell(): JSX.Element {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['notifications'], queryFn: () => notificationsApi.list() });
  const [open, setOpen] = useState(false);

  const connect = useSocketStore((s) => s.connect);
  const socket = useSocketStore((s) => s.socket);
  useEffect(() => { connect(); }, [connect]);
  useEffect(() => {
    if (!socket) return;
    function onNotif(n: NotifRow): void {
      qc.setQueryData<{ items: NotifRow[] } | undefined>(['notifications'], (prev) => ({
        items: prev ? [n, ...prev.items.filter((i) => i.id !== n.id)] : [n],
      }));
    }
    socket.on('notification', onNotif);
    return () => { socket.off('notification', onNotif); };
  }, [socket, qc]);

  const unread = data?.items.filter((i) => !i.isRead).length ?? 0;
  const recent = data?.items.slice(0, 10) ?? [];

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} aria-label="Notifications" className="relative inline-flex items-center justify-center rounded-md min-w-11 min-h-11 hover:bg-muted">
        <Icon name="notifications" size={20} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-50 mt-2 w-80 rounded-md border border-border bg-background shadow-lg">
            <header className="flex items-center justify-between px-3 py-2 border-b border-border">
              <span className="text-sm font-medium">Notifications</span>
              <Link to="/inbox" onClick={() => setOpen(false)} className="text-xs text-primary hover:underline">View all</Link>
            </header>
            <ul className="max-h-80 overflow-auto">
              {recent.length === 0 && <li className="p-3 text-sm text-muted-foreground">No notifications.</li>}
              {recent.map((n) => (
                <li key={n.id}>
                  <a
                    href={n.link ?? '#'}
                    onClick={() => void notificationsApi.markRead(n.id).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }))}
                    className={`block px-3 py-2 hover:bg-muted ${n.isRead ? 'opacity-70' : ''}`}
                  >
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
